import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { Menu, Icon } from 'antd';
import { dashboardContentVariantSelector } from '../../../scenes/Organisation/OrganisationSelectors';
import { isBcmFirst, getBcmLabels } from '../../../scenes/Dashboard/scenes/Plan/planSections';

const { SubMenu } = Menu;

const PlanMenu = ({ selectedKey, dashboardContentVariant }) => {
  const isAvivaVariant = isBcmFirst(dashboardContentVariant);
  const { program: programLabel, analysis: analysisLabel, design: designLabel, implementation: implementationLabel, validation: validationLabel } = getBcmLabels(dashboardContentVariant);
  // Nested walkthrough URLs should keep the Testing & Exercising item selected
  const activeKey = selectedKey.startsWith('/dashboard/testing-exercising')
    ? '/dashboard/testing-exercising'
    : selectedKey;
  const bcmTitle = (
    <span>
      <Icon type="area-chart" aria-hidden />
      <span title="Business Continuity Management">Business Continuity Management</span>
    </span>
  );

  const ermTitle = (
    <span>
      <Icon type="exception" aria-hidden />
      <span title="Enterprise Risk Management">Enterprise Risk Management</span>
    </span>
  );

  const BcmSubmenus = [
    <SubMenu key="policy_program" title={programLabel} className="sider-menu-program">
      <Menu.Item key="/dashboard/business-units">
        <Link to="/dashboard/business-units">Business Units</Link>
      </Menu.Item>
      <Menu.Item key="/dashboard/teams">
        <Link to="/dashboard/teams">Teams</Link>
      </Menu.Item>
      <Menu.Item key="/dashboard/policy" title="Business Continuity Policy Accountable Members">
        <Link to="/dashboard/policy">Accountable Members</Link>
      </Menu.Item>
    </SubMenu>,
    <SubMenu key="analysis" title={analysisLabel} className="sider-menu-analysis">
      <Menu.Item key="/dashboard/business-impact-analysis/strategic" title="Strategic Business Impact Analysis">
        <Link to="/dashboard/business-impact-analysis/strategic">Strategic Business Impact Analysis</Link>
      </Menu.Item>
      <Menu.Item key="/dashboard/business-impact-analysis/tactical" title="Tactical Business Impact Analysis">
        <Link to="/dashboard/business-impact-analysis/tactical">Tactical Business Impact Analysis</Link>
      </Menu.Item>
    </SubMenu>,
    <SubMenu key="design" title={designLabel} className="sider-menu-design">
      <Menu.Item key="/dashboard/business-impact-analysis/operational" title="Operational Business Impact Analysis">
        <Link to="/dashboard/business-impact-analysis/operational">Operational Business Impact Analysis</Link>
      </Menu.Item>
      <Menu.Item key="/dashboard/recovery-strategy">
        <Link to="/dashboard/recovery-strategy">Recovery Strategy</Link>
      </Menu.Item>
    </SubMenu>,
    <SubMenu key="implementation" title={implementationLabel} className="sider-menu-implementation">
      <Menu.Item key="/dashboard/plan-document" title="Business Continuity Plan">
        <Link to="/dashboard/plan-document">
          <Icon type="file-text" aria-hidden />
          Business Continuity Plan
        </Link>
      </Menu.Item>
    </SubMenu>,
    <SubMenu key="validation" title={validationLabel} className="sider-menu-validation">
      <Menu.Item key="/dashboard/testing-exercising" title="Validation">
        <Link to="/dashboard/testing-exercising">
          <Icon type="safety-certificate" aria-hidden />
          Validation
        </Link>
      </Menu.Item>
    </SubMenu>,
  ];

  return (
    <Menu mode="inline" inlineIndent={8} selectedKeys={[activeKey]} className="sider-menu">
      <Menu.Item key="/dashboard">
        <Link to="/dashboard">
          <Icon type="dashboard" aria-hidden />
          <span>Dashboard</span>
        </Link>
      </Menu.Item>

      {isAvivaVariant
        ? [
            <SubMenu key="business_continuity_management" title={bcmTitle}>
              {BcmSubmenus}
            </SubMenu>,
            <SubMenu key="enterprise_risk_management" title={ermTitle}>
              <Menu.Item key="/dashboard/risk/exposures">
                <Link to="/dashboard/risk/exposures">Risk Exposures</Link>
              </Menu.Item>
              <Menu.Item key="/dashboard/risk/assessment">
                <Link to="/dashboard/risk/assessment">Risk and Threat Assessment</Link>
              </Menu.Item>
              <Menu.Item key="/dashboard/risk/register">
                <Link to="/dashboard/risk/register">Risk Register</Link>
              </Menu.Item>
              <Menu.Item key="/dashboard/risk/report">
                <Link to="/dashboard/risk/report">
                  <Icon type="file-text" aria-hidden />
                  Risk &amp; Insurance Report
                </Link>
              </Menu.Item>
            </SubMenu>,
          ]
        : [
            <SubMenu key="enterprise_risk_management" title={ermTitle}>
              <Menu.Item key="/dashboard/risk/exposures">
                <Link to="/dashboard/risk/exposures">Risk Exposures</Link>
              </Menu.Item>
              <Menu.Item key="/dashboard/risk/assessment">
                <Link to="/dashboard/risk/assessment">Risk and Threat Assessment</Link>
              </Menu.Item>
              <Menu.Item key="/dashboard/risk/register">
                <Link to="/dashboard/risk/register">Risk Register</Link>
              </Menu.Item>
              <Menu.Item key="/dashboard/risk/report">
                <Link to="/dashboard/risk/report">
                  <Icon type="file-text" aria-hidden />
                  Risk &amp; Insurance Report
                </Link>
              </Menu.Item>
            </SubMenu>,
            <SubMenu key="business_continuity_management" title={bcmTitle}>
              {BcmSubmenus}
            </SubMenu>,
          ]}

      <Menu.Item key="/dashboard/activation-levels">
        <Link to="/dashboard/activation-levels">
          <Icon type="setting" aria-hidden />
          <span>Activation Levels</span>
        </Link>
      </Menu.Item>
      <Menu.Item key="/dashboard/documents">
        <Link to="/dashboard/documents">
          <Icon type="folder-open" aria-hidden />
          <span>Documents</span>
        </Link>
      </Menu.Item>
      {isAvivaVariant && (
        <SubMenu
          key="aviva_resources"
          title={
            <span>
              <Icon type="global" aria-hidden />
              <span>Aviva Resources</span>
            </span>
          }
        >
          <Menu.Item key="aviva_arms_website" className="sider-menu-external-item">
            <a
              href="https://www.aviva.co.uk/risksolutions/"
              target="_blank"
              rel="noopener noreferrer"
              className="sider-menu-external-link"
              aria-label="ARMS Website (opens in a new tab)"
            >
              <Icon type="home" aria-hidden />
              <span>ARMS Website</span>
              <Icon type="export" className="sider-menu-external-icon" aria-hidden />
            </a>
          </Menu.Item>
          <Menu.Item key="aviva_bc_lps" className="sider-menu-external-item">
            <a
              href="https://www.aviva.co.uk/risksolutions/guidance-by-risk-type/business-continuity-planning/"
              target="_blank"
              rel="noopener noreferrer"
              className="sider-menu-external-link"
              aria-label="Business Continuity LPS (opens in a new tab)"
            >
              <Icon type="file-protect" aria-hidden />
              <span>Business Continuity LPS</span>
              <Icon type="export" className="sider-menu-external-icon" aria-hidden />
            </a>
          </Menu.Item>
          <Menu.Item key="aviva_bci" className="sider-menu-external-item">
            <a
              href="https://www.thebci.org/"
              target="_blank"
              rel="noopener noreferrer"
              className="sider-menu-external-link"
              aria-label="Business Continuity Institute (opens in a new tab)"
            >
              <Icon type="global" aria-hidden />
              <span>Business Continuity Institute</span>
              <Icon type="export" className="sider-menu-external-icon" aria-hidden />
            </a>
          </Menu.Item>
          <Menu.Item key="aviva_bi_calculator" className="sider-menu-external-item">
            <a
              href="https://www.aviva.co.uk/risksolutions/PreventionSolutions/business-interruption-calculator/"
              target="_blank"
              rel="noopener noreferrer"
              className="sider-menu-external-link"
              aria-label="BI Calculator (opens in a new tab)"
            >
              <Icon type="calculator" aria-hidden />
              <span>BI Calculator</span>
              <Icon type="export" className="sider-menu-external-icon" aria-hidden />
            </a>
          </Menu.Item>
        </SubMenu>
      )}
      <Menu.Item key="/dashboard/glossary">
        <Link to="/dashboard/glossary">
          <Icon type="book" aria-hidden />
          <span>Glossary</span>
        </Link>
      </Menu.Item>
    </Menu>
  );
};

const mapStateToProps = (state) => ({
  dashboardContentVariant: dashboardContentVariantSelector(state),
});

export default connect(mapStateToProps)(PlanMenu);
